import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, Button, Grid, Card, CardContent, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, TablePagination, Chip, Snackbar, Alert, styled, Dialog, DialogContent, IconButton, TextField, MenuItem, InputAdornment, Divider } from "@mui/material";
import { TrendingUp, HowToReg, PersonOff, AlarmOn, Close as CloseIcon, InfoOutlined, Search, RestartAlt } from '@mui/icons-material';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import ViewLeaveRequest from './viewLeaveRequest';
import { formatDate } from '../../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../common/spinner';

const StatCard = ({ title, value, icon, color }) => (
    <Card
        sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: '100%',
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            border: 'none'
        }}
    >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600', opacity: 0.9, display: 'block' }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                        {value}
                    </Typography>
                </Box>
                <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', p: 1.5, borderRadius: '12px', display: 'flex', backdropFilter: 'blur(4px)' }}>
                    {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                </Box>
            </Stack>
        </CardContent>
        <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.1)', zIndex: 0 }} />
    </Card>
);

const ReasonCell = styled(TableCell)({
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#64748b',
    fontSize: '0.825rem'
});

function LeaveManagement() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({ page: 0, limit: 10, totalCount: 0 });
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('ALL');

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.id;
    const organizationId = userData?.organization_id;
    const userRole = userData?.user_type; // 'Manager' or 'Team Lead'
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';

    const MONTHS = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const YEARS = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

    // NEW INITIALIZATION LOGIC
    const [monthFilter, setMonthFilter] = useState(MONTHS[new Date().getMonth()]);
    const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());

    const fetchLeaveRequests = useCallback(async () => {
        if (!userId || !organizationId) return;

        try {
            setLoading(true);

            let requestUrl = "";
            const commonParams = {
                search: searchTerm || "null",
                status: statusFilter === 'ALL' ? "null" : statusFilter,
                page: pagination.page + 1,
                limit: pagination.limit,
                month: monthFilter === 'ALL' ? "null" : monthFilter,
                year: yearFilter === 'ALL' ? "null" : yearFilter
            };

            if (userRole === 'Team Lead') {
                requestUrl = API_ENDPOINTS.fetchLeaveRequestsByTeamLead(userId, commonParams);
            } else {
                requestUrl = API_ENDPOINTS.fetchAllLeaveRequests({
                    ...commonParams,
                    organization_id: organizationId,
                    report_manager_id: userId
                });
            }

            const response = await getData(requestUrl);

            if (response.status === "SUCCESS") {
                const { data, stats: apiStats, pagination: apiPagination } = response.message;

                const transformedData = data.map((request) => ({
                    id: request.id,
                    // Accessing nested employee object from your JSON
                    name: `${request.employee?.first_name || ''} ${request.employee?.last_name || ''}`,
                    empId: request.employee?.emp_id || 'N/A',
                    department: request.employee?.department || 'N/A',
                    leaveType: request.hrms_leave_type?.leave_type || 'N/A',
                    reason: request.reason || 'No reason provided',
                    status: request.status,
                    duration: request.actual_leave_days || 0,
                    startDate: request.start_date,
                    endDate: request.end_date,
                    // Pass the whole original object if ViewLeaveRequest needs more details
                    originalData: request
                }));

                setLeaveRequests(transformedData);

                // Using "totalItems" from your JSON pagination object
                setPagination(prev => ({
                    ...prev,
                    totalCount: Number(apiPagination?.totalItems || 0)
                }));

                // Matching your JSON stat keys: totalRequests, pending, approved, rejected
                if (apiStats) {
                    setStats({
                        total: apiStats.totalRequests || 0,
                        pending: apiStats.pending || 0,
                        approved: apiStats.approved || 0,
                        rejected: apiStats.rejected || 0
                    });
                }
            }
        } catch (error) {
            setSnackbar({ open: true, message: error.message || 'Failed to fetch leaves', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [userId, organizationId, userRole, pagination.page, pagination.limit, statusFilter, searchTerm, monthFilter, yearFilter]);

    useEffect(() => {
        fetchLeaveRequests();
    }, [fetchLeaveRequests]);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED': return 'success';
            case 'PENDING': return 'warning';
            case 'REJECTED': return 'error';
            default: return 'default';
        }
    };

    const handleRowClick = (request) => {
        setSelectedRequest(request);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedRequest(null);
    };

    const handleReset = () => {
        setSearchTerm("");
        setStatusFilter('ALL');
        // Setting back to current date defaults
        setMonthFilter(MONTHS[new Date().getMonth()]);
        setYearFilter(new Date().getFullYear().toString());
        // Reset pagination to first page
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const LeaveMobileCard = ({ row }) => (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>
                            {row.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">ID: {row.empId}</Typography>
                    </Box>
                    <Chip
                        label={row.status}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            bgcolor: getStatusColor(row.status).bg,
                            color: getStatusColor(row.status).color,
                            border: `1px solid ${getStatusColor(row.status).border || '#cbd5e1'}`
                        }}
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Chip label={row.leaveType} size="small" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        {row.duration} Days
                    </Typography>
                </Stack>

                <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {row.reason}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="caption" display="block" fontWeight="600">{formatDate(row.startDate)}</Typography>
                        <Typography variant="caption" color="textSecondary">to {formatDate(row.endDate)}</Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => handleRowClick(row)}>View</Button>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 1 }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={{ xs: 1.5, sm: 0 }} sx={{ mb: 4 }} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h5" fontWeight="700" color="#0f172a">
                        {userRole} Leave Management
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                        {userRole === 'Team Lead' ? 'Manage your team\'s leave requests' : 'Manage organization-wide leave requests'}
                    </Typography>
                </Box>
                <Button variant="contained" disableElevation
                    onClick={() => navigate(`${roleBasePath}/manage-leave-requests`)}
                    sx={{ borderRadius: '10px', textTransform: 'none', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
                    My Leaves
                </Button>
            </Stack>

            <Grid container spacing={3} mb={4}>
                {[
                    { title: 'Total Requests', value: stats.total, icon: <TrendingUp />, color: '#6366F1' },
                    { title: 'Pending Approval', value: stats.pending, icon: <AlarmOn />, color: '#F59E0B' },
                    { title: 'Accepted', value: stats.approved, icon: <HowToReg />, color: '#10B981' },
                    { title: 'Rejected', value: stats.rejected, icon: <PersonOff />, color: '#EF4444' }
                ].map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                        <StatCard title={card.title} value={card.value} icon={card.icon} color={card.color} />
                    </Grid>
                ))}
            </Grid>

            {/* Filter & Search Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>Leave Requests</Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                    <TextField
                        placeholder="Search employee..."
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchLeaveRequests()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Year"
                        value={yearFilter}
                        onChange={(e) => {
                            setYearFilter(e.target.value);
                            setPagination(prev => ({ ...prev, page: 0 }));
                        }}
                    >
                        <MenuItem value="ALL">All Years</MenuItem>
                        {YEARS.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Month"
                        value={monthFilter}
                        onChange={(e) => {
                            setMonthFilter(e.target.value);
                            setPagination(prev => ({ ...prev, page: 0 }));
                        }}
                    >
                        <MenuItem value="ALL">All Months</MenuItem>
                        {MONTHS.map(month => (
                            <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPagination(prev => ({ ...prev, page: 0 }));
                        }}
                    >
                        <MenuItem value="ALL">All Status</MenuItem>
                        <MenuItem value="PENDING">Pending</MenuItem>
                        <MenuItem value="APPROVED">Approved</MenuItem>
                        <MenuItem value="REJECTED">Rejected</MenuItem>
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 1 }}>
                    <Button fullWidth onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        <RestartAlt sx={{ mr: { xs: 1, md: 0, xl: 1 } }} />
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'none', xl: 'inline' } }}>
                            Reset
                        </Box>
                    </Button>
                </Grid>
            </Grid>

            {/* Mobile cards */}
            <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><Loader /></Box>
                ) : leaveRequests.length > 0 ? leaveRequests.map((row) => (
                    <LeaveMobileCard key={row.id} row={row} />
                )) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <InfoOutlined sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                        <Typography color="textSecondary">No leave requests found.</Typography>
                    </Box>
                )}
            </Stack>

            {/* Table */}
            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '12px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                {['Employee', 'Leave Type', 'Duration', 'Timeline', 'Reason', 'Status', 'Action'].map(h => (
                                    <TableCell key={h} sx={{ color: '#1D4ED8', fontWeight: 700 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Loader /></TableCell></TableRow>
                            ) : leaveRequests.length > 0 ? (
                                leaveRequests.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell sx={{ color: '#1E293B' }}>
                                            <Typography variant="body2" fontWeight="600" color="#1e293b">
                                                {row.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {row.empId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={row.leaveType} size="small" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                                        </TableCell>
                                        <TableCell>{row.duration} Days</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block" fontWeight="600">{formatDate(row.startDate)}</Typography>
                                            <Typography variant="caption" color="textSecondary">to {formatDate(row.endDate)}</Typography>
                                        </TableCell>
                                        <ReasonCell title={row.reason}>{row.reason}</ReasonCell>
                                        <TableCell>
                                            <Chip
                                                label={row.status}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.7rem',
                                                    bgcolor: getStatusColor(row.status).bg,
                                                    color: getStatusColor(row.status).color,
                                                    border: `1px solid ${getStatusColor(row.status).border || '#cbd5e1'}`
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button size="small" variant="outlined" onClick={() => handleRowClick(row)}>View</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                    <InfoOutlined sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                    <Typography color="textSecondary">No leave requests found.</Typography>
                                </TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <TablePagination
                component="div"
                count={pagination.totalCount}
                page={pagination.page}
                onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value, 10), page: 0 }))}
                rowsPerPageOptions={[5, 10, 25]}
            />

            {/* Notifications & Dialogs */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>

            <Dialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: '16px' },
                        m: { xs: 0, sm: 4 },
                        width: { xs: '100%', sm: 'auto' },
                        maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" fontWeight="700">Leave Request Details</Typography>
                    <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {selectedRequest && <ViewLeaveRequest data={selectedRequest} onClose={handleCloseDialog} refresh={fetchLeaveRequests} />}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default LeaveManagement;